import { Navigations } from "@/AppRoutes";
import { scrollToTop } from "@/utils";
import {
  Facebook,
  Instagram,
  Mail,
  MapPin,
  Music,
  Phone,
  X,
  Youtube,
} from "lucide-react";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const HeaderFullScreenMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-white overflow-y-auto antialiased">
      {/* Ambient background glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-church-gold-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 min-h-screen flex flex-col justify-between relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 pb-6 sm:pb-8 border-b border-white/10">
          <Link
            to="/"
            className="group shrink-0"
            onClick={() => {
              onClose();
              scrollToTop();
            }}
          >
            <LogoFull style={{ filter: "invert(1)" }} />
          </Link>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-md border border-white/10 text-xs sm:text-sm font-bold tracking-wider uppercase shrink-0"
            aria-label="Fermer le menu"
          >
            <span>Fermer</span>
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Primary Navigation */}
        <nav className="my-auto py-8 sm:py-12">
          <ul className="space-y-4 sm:space-y-6 md:space-y-8">
            {Navigations.map((item) => {
              const isActive =
                window.location.pathname === item.href ||
                (item.href !== "/" &&
                  window.location.pathname.startsWith(item.href));
              const isHymns =
                item.href === "/chants-desperance" ||
                item.href === "/chant-desperance" ||
                item.id === "chants-desperance";

              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`group inline-flex items-center flex-wrap gap-2.5 sm:gap-4 text-2xl xs:text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight transition-all duration-300 ${
                      isActive
                        ? "text-church-gold-400 font-bold translate-x-1.5 sm:translate-x-2"
                        : "text-white/90 hover:text-church-gold-300 hover:translate-x-1.5 sm:hover:translate-x-2"
                    }`}
                    onClick={() => {
                      onClose();
                      scrollToTop();
                    }}
                  >
                    <span>{item.name}</span>
                    {isHymns && (
                      <span className="relative inline-flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 bg-white/15 text-white shadow-md border border-white/20 text-xs sm:text-sm font-sans tracking-normal align-middle">
                        <span
                          className={`flex items-center justify-center w-5 h-5 rounded-full ${
                            isActive
                              ? "bg-church-gold-500 text-slate-950"
                              : "bg-white/20 text-white"
                          }`}
                        >
                          <Music className="w-3 h-3" />
                        </span>
                        <span className="font-bold text-xs tracking-wider uppercase">
                          Cantiques
                        </span>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-amber-400"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
                        </span>
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer Contact & Social Section */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-xs sm:text-sm text-slate-300">
          {/* Church Details */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-church-gold-400" />
              <span>11800 4e Avenue, Montréal, QC H1E 5Y2</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-church-gold-400" />
              <a
                href="tel:5143224921"
                className="hover:text-white transition-colors"
              >
                (514) 322-4921
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-church-gold-400" />
              <a
                href="mailto:egarizim@gmail.com"
                className="hover:text-white transition-colors"
              >
                egarizim@gmail.com
              </a>
            </div>
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.youtube.com/@eglisegarizim1"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/10 hover:bg-red-600 text-white transition-all"
              aria-label="YouTube"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/eglisegarizim/?locale=fr_CA"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/10 hover:bg-blue-600 text-white transition-all"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/eddgarizim/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/10 hover:bg-pink-600 text-white transition-all"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderFullScreenMenu;
